import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import { FormsModule, FormGroup, FormBuilder, AbstractControl, Validators, ValidatorFn } from '@angular/forms';
import {AppCommonModule} from '@app/components/app.common.module';
import {ModalService} from '@app/components/modal/modal.service';
import { Constants } from '@app/constants/Constants';
import {FinancialCondition} from "@app/pages/quote/standard-quote/model/financial-setup.model";

@Component({
  selector: 'app-condition-template',
  standalone: true,
  templateUrl: './condition-template.component.html',
  styleUrls: ['./condition-template.component.css'],

  imports: [FormsModule, AppCommonModule, CommonModule
  ]
})
export class ConditionTemplateComponent implements OnInit {
  @Output() save = new EventEmitter<any>();
  @Input() data: FinancialCondition;
  @Input() financialConditions: FinancialCondition[] = [];
  @Input() isEdit: boolean = false;
  @Input() allowCapitalGrace: boolean = false;
  @Input() allowInterestGrace: boolean = false;
  form: FormGroup;
  isTermError: boolean = false;
  minTerm: number = Constants.MIN_TERMS_FOR_NORMAL_PRODUCT;
  maxTerm: number = Constants.MAX_TERMS_FOR_NORMAL_PRODUCT;

  constructor(private fb: FormBuilder, private modalService: ModalService) {
  }

  ngOnChanges() {
    if (this.isEdit) {
      this.updateForm();
    }
    // Update validators when preferences change
    if (this.form) {
      this.updateGracePeriodValidation();
    }
  }

  preventNonInteger(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', "ArrowUp", "ArrowDown"
    ];
    if (allowedKeys.includes(event.key)) return;
    const isDigit = /\d/.test(event.key);
    // Block anything that is not a digit
    if (!isDigit) {
      event.preventDefault();
    }
  }

  onPasteInteger(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!/^(0|[1-9][0-9]*)$/.test(text)) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    if (!this.isEdit) {
      this.createForm();
    } else {
      this.updateForm();
    }
  }

  static validateMaxGreaterThanMin(minControlName: string, maxControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const minControl = formGroup.get(minControlName);
      const maxControl = formGroup.get(maxControlName);

      if (minControl && maxControl) {
        const minValue = minControl.value;
        const maxValue = maxControl.value;

        if (minValue !== null && maxValue !== null && maxValue < minValue) {
          return { 'maxLessThanMin': true };
        }
      }

      return null;
    };
  }

  static validateGracePeriodLessThanTerm(termControlName: string, gracePeriodControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const termControl = formGroup.get(termControlName);
      const gracePeriodControl = formGroup.get(gracePeriodControlName);

      if (termControl && gracePeriodControl) {
        const termValue = termControl.value;
        const gracePeriodValue = gracePeriodControl.value;

        if (termValue !== null && gracePeriodValue !== null && gracePeriodValue >= termValue) {
          return { 'gracePeriodTooHigh': true };
        }
      }

      return null;
    };
  }

  static validateWarrantyDepositBasedOnTerm(termControlName: string, warrantyDepositControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const termControl = formGroup.get(termControlName);
      const warrantyDepositControl = formGroup.get(warrantyDepositControlName);

      if (termControl && warrantyDepositControl) {
        const termValue = termControl.value;
        const warrantyDepositValue = warrantyDepositControl.value;

        if (termValue !== null && warrantyDepositValue !== null) {
          const maxWarrantyDeposit = termValue - 1;

          if (warrantyDepositValue < 0 || warrantyDepositValue > maxWarrantyDeposit) {
            return { 'warrantyDepositOutOfRange': true };
          }
        }
      }

      return null;
    };
  }

  /**
   * Creates a validator function that validates grace period against preferences
   * Note: This should be called as an instance method to access current preference values
   */
  validateGracePeriodWithPreferences(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const gracePeriodControl = formGroup.get('gracePeriod');

      if (gracePeriodControl) {
        const gracePeriodValue = gracePeriodControl.value;
        const hasPreferences = this.allowCapitalGrace || this.allowInterestGrace;

        // Si no hay preferencias habilitadas y se intenta ingresar un período de gracia > 0
        if (!hasPreferences && gracePeriodValue !== null && gracePeriodValue !== undefined && gracePeriodValue > 0) {
          return { 'gracePeriodWithoutPreferences': true };
        }
      }

      return null;
    };
  }

  /**
   * Checks if both preferences are unchecked
   */
  arePreferencesDisabled(): boolean {
    return !this.allowCapitalGrace && !this.allowInterestGrace;
  }

  valueChanges(){
    if (this.form.controls['term']) {
      this.form.controls['term'].valueChanges.subscribe((term) => {
        if(term) {
          const termNumber = parseInt(term);
          this.isTermError = !!this.financialConditions.find((condition) => condition.term === termNumber);
        }
        // Revalidar el formulario cuando cambie el plazo para actualizar la validación de warrantyDepositPercentage
        this.form.updateValueAndValidity();
      });
    }

    if (this.form.controls['warrantyDepositPercentage']) {
      this.form.controls['warrantyDepositPercentage'].valueChanges.subscribe(() => {
        // Revalidar el formulario cuando cambie warrantyDepositPercentage
        this.form.updateValueAndValidity();
      });
    }
  }
  updateForm() {
    this.form = this.fb.group({
      id: [this.data.id, []],
      term: [this.data.term, [Validators.required, Validators.pattern('^(1[0-7][0-9]|[1-9][0-9]|[1-9])$')]],
      minDownPaymentPercentage: [this.data.minDownPaymentPercentage, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      maxDownPaymentPercentage: [this.data.maxDownPaymentPercentage, [Validators.required, Validators.pattern('^(100|[1-9]?[0-9](\\.[0-9]{1,2})?)$')]],
      nominalRate: [this.data.nominalRate, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      insuranceNominalRate: [this.data.insuranceNominalRate, []],
      openingFeePercentage: [this.data.openingFeePercentage, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      purchasePercentage: [this.data.purchasePercentage, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      paymentIncentive: [this.data.paymentIncentive, []],
      warrantyDepositPercentage: [this.data.warrantyDepositPercentage, [Validators.required]],
      gracePeriod: [this.data.gracePeriod, [Validators.required, Validators.pattern('^(0|[1-9][0-9]*)$')]],
      gracePeriodInterest: [this.data.gracePeriodInterest, []],
      allowPaymentDay: [this.data.allowPaymentDay, []],
      active: [this.data.active, []],
    }, { validators: [
      ConditionTemplateComponent.validateMaxGreaterThanMin('minDownPaymentPercentage', 'maxDownPaymentPercentage'),
      ConditionTemplateComponent.validateGracePeriodLessThanTerm('term', 'gracePeriod'),
      ConditionTemplateComponent.validateWarrantyDepositBasedOnTerm('term', 'warrantyDepositPercentage'),
      this.validateGracePeriodWithPreferences()
    ] });
    this.valueChanges();
    this.updateGracePeriodValidation();
  }


  validateTermUsed(control: AbstractControl): { [key: string]: any } | null  {
      const term = control.value;
      if (!term) return null;
      const find = this.financialConditions.find((condition) => condition.term === term);
      return find ? {used: true} : null;
  }

  createForm() {
    this.form = this.fb.group({
      id: [null, []],
      term: [null, [Validators.required, Validators.min(Constants.MIN_TERMS_FOR_NORMAL_PRODUCT), Validators.max(Constants.MAX_TERMS_FOR_NORMAL_PRODUCT)]],
      minDownPaymentPercentage: [null, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      maxDownPaymentPercentage: [null, [Validators.required, Validators.pattern('^(100|[1-9]?[0-9](\\.[0-9]{1,2})?)$')]],
      nominalRate: [null, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      insuranceNominalRate: [null, []],
      openingFeePercentage: [null, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      purchasePercentage: [null, [Validators.required, Validators.pattern('^([0-9]{1,2}(\\.[0-9]{1,2})?)$')]],
      warrantyDepositPercentage: [null, [Validators.required]],
      gracePeriod: [null, [Validators.required, Validators.pattern('^(0|[1-9][0-9]*)$')]],
      gracePeriodInterest: [null, []],
      allowPaymentDay: [false],
      paymentIncentive: [null, []],
      active: [false, []],
    }, { validators: [
      ConditionTemplateComponent.validateMaxGreaterThanMin('minDownPaymentPercentage', 'maxDownPaymentPercentage'),
      ConditionTemplateComponent.validateGracePeriodLessThanTerm('term', 'gracePeriod'),
      ConditionTemplateComponent.validateWarrantyDepositBasedOnTerm('term', 'warrantyDepositPercentage'),
      this.validateGracePeriodWithPreferences()
    ] });
    this.valueChanges();
    this.updateGracePeriodValidation();
  }

  close() {
    this.modalService.closeModal();
  }

  saved() {
    const formValue = this.form.getRawValue();

    if (this.arePreferencesDisabled() && formValue.gracePeriod > 0) {
      this.form.get('gracePeriod')?.markAsTouched();
      this.form.get('gracePeriod')?.markAsDirty();
      this.form.updateValueAndValidity();
      return;
    }

    if (this.hasRealValidationErrors()) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    if (this.arePreferencesDisabled()) {
      formValue.gracePeriod = 0;
    }

    this.save.emit(formValue);
    this.close()
  }

  isTermUsed(): boolean {
    const term = this.form.controls['term'].value;
    if(!term) return false;
    const find = this.financialConditions.find((condition) => condition.term === term);
    return !!find;
  }

  hasTermLimitError() {
    return (this.form.get('term')?.hasError('min') || this.form.get('term')?.hasError('max')) && !this.form.get('term')?.hasError('required') && this.form.get('term')?.touched;
  }


  updateGracePeriodValidation() {
    if (!this.form) return;

    const gracePeriodControl = this.form.get('gracePeriod');
    if (!gracePeriodControl) return;

    const hasPreferences = this.allowCapitalGrace || this.allowInterestGrace;

    // Update form validators
    const validators = [
      ConditionTemplateComponent.validateMaxGreaterThanMin('minDownPaymentPercentage', 'maxDownPaymentPercentage'),
      ConditionTemplateComponent.validateGracePeriodLessThanTerm('term', 'gracePeriod'),
      ConditionTemplateComponent.validateWarrantyDepositBasedOnTerm('term', 'warrantyDepositPercentage'),
      this.validateGracePeriodWithPreferences()
    ];

    // Remove old validators and set new ones
    this.form.clearValidators();
    this.form.setValidators(validators);

    gracePeriodControl.enable({ emitEvent: false });

    this.form.updateValueAndValidity();
    gracePeriodControl.updateValueAndValidity();
  }

  hasGracePeriodPreferencesError(): boolean {
    if (!this.form) return false;
    const gracePeriodControl = this.form.get('gracePeriod');
    const gracePeriodValue = gracePeriodControl?.value;
    return this.form.hasError('gracePeriodWithoutPreferences') &&
           gracePeriodValue !== null &&
           gracePeriodValue !== undefined &&
           gracePeriodValue > 0;
  }

  hasRealValidationErrors(): boolean {
    if (!this.form) return false;

    const gracePeriodControl = this.form.get('gracePeriod');
    const gracePeriodValue = gracePeriodControl?.value;
    const hasOnlyGracePeriodError = this.form.hasError('gracePeriodWithoutPreferences') &&
                                    (gracePeriodValue === 0 || gracePeriodValue === null || gracePeriodValue === undefined);

    const hasFieldErrors = Object.keys(this.form.controls).some(key => {
      const control = this.form.get(key);
      return control && control.invalid && control.errors;
    });

    if (hasOnlyGracePeriodError && !hasFieldErrors) {
      return false;
    }

    return this.form.invalid;
  }

}
